import { getAuthorizationToken } from '../../../../common/sgwt-widgets-utils';
import type {
  DxAssistantContext,
  SgwtChatbotAdapter,
  SgwtChatbotContext,
  SgwtChatbotWidgetPublishMessageHandler,
} from '../../../../common/types/sgwt-chatbot.adapter';
import type { SgwtChatbotConversation, SgwtChatbotMessage, SgwtChatbotUserHistory } from '../../shared/chatbot.types';
import type { DxAssistantConversation, DxAssistantConversationsHistory, DxAssistantPrompt, DxAssistantPromptResponse } from './dx-assistant.types';
import { DxConverter } from './dx-assistant.utils';

const API_URL = {
  development: 'https://dx-dev.fr.world.socgen/api/assistant/api/v1/chatbot',
  homologation: 'https://developer-uat.fr.world.socgen/api/assistant/api/v1/chatbot',
  production: 'https://developer.fr.world.socgen/api/assistant/api/v1/chatbot',
};

// cf. `help-center.endpoints.ts` file
const FEEDBACK_URL = {
  development: 'https://sgwt-api-sgwt-help-center-dev.sgmarkets.world.socgen/api/v1/feedback',
  homologation: 'https://sgwt-api-sgwt-help-center-uat.sgmarkets.world.socgen/api/v1/feedback',
  production: 'https://sgwt-api-sgwt-help-center.sgmarkets.world.socgen/api/v1/feedback',
};

export class DxAssistantAdapter implements SgwtChatbotAdapter {
  private readonly apiUrl: string;

  // This handler is used to send messages asynchronously to the chat widget
  private messageHandler: SgwtChatbotWidgetPublishMessageHandler | null = null;

  constructor(context: SgwtChatbotContext) {
    const env = (context as DxAssistantContext).apiEnvironment?.toLowerCase() ?? '';
    if (['prod', 'prd', 'production'].includes(env)) {
      this.apiUrl = API_URL.production;
    } else if (['homologation', 'uat'].includes(env)) {
      this.apiUrl = API_URL.homologation;
    } else if (['dev', 'development'].includes(env)) {
      this.apiUrl = API_URL.development;
    } else {
      this.apiUrl = API_URL[context.environment];
    }
  }

  public setPublishMessageHandler(handler: SgwtChatbotWidgetPublishMessageHandler) {
    this.messageHandler = handler;
  }

  get name(): string {
    return 'dx-assistant';
  }

  private async callApi<T>(url: string, method: 'GET' | 'POST' = 'GET', body?: string): Promise<T> {
    return fetch(url, {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        authorization: getAuthorizationToken() || '',
      },
      method,
      mode: 'cors',
      body,
    })
      .then((response: Response) => {
        if (response.ok) {
          return response;
        }
        throw new Error(response.statusText);
      })
      .then((response) => response.json());
  }

  public async onUserMessage(rawContext: SgwtChatbotContext, userMessage: SgwtChatbotMessage): Promise<SgwtChatbotMessage | null> {
    const context = rawContext as DxAssistantContext;
    // Convert SgwtChatbotUserMessage to DxAssistantPrompt
    const dxPrompt: DxAssistantPrompt = {
      userMessage: userMessage.message.content,
      conversationId: userMessage.conversationId,
      ...context,
      knowledgeSpace: context?.knowledgeSpace ?? 'dx',
    };

    const dxResponse = await this.callApi<DxAssistantPromptResponse>(`${this.apiUrl}/_prompt`, 'POST', JSON.stringify(dxPrompt));

    if (!dxResponse) {
      return null;
    }

    return DxConverter.convertPromptResponse(dxResponse);
  }

  public async getHistory(_context?: SgwtChatbotContext): Promise<SgwtChatbotUserHistory> {
    const archived = false;
    const size = 10;
    const sort = '-updated-at'; // updated-at, but in descending order
    const history = await this.callApi<DxAssistantConversationsHistory>(
      `${this.apiUrl}/conversations/_my?includeArchived=${archived}&pageSize=${size}&sort=${sort}`,
    );

    if (!history) {
      return { conversations: [] };
    }

    return {
      conversations: history.conversations.map(DxConverter.convertConversation),
    };
  }

  public async getConversation(_context: SgwtChatbotContext, conversationId: string): Promise<SgwtChatbotConversation | null> {
    const conversation = await this.callApi<DxAssistantConversation>(`${this.apiUrl}/conversations/${conversationId}`);
    if (conversation === null) {
      return null;
    }
    return DxConverter.convertConversation(conversation);
  }

  // Post the user reaction as a feedback message to the Help Center API
  public async onUserFeedback(
    context: SgwtChatbotContext,
    message: SgwtChatbotMessage,
    reaction: 'like' | 'dislike',
    explanations: string,
    _conversation: SgwtChatbotMessage[],
  ): Promise<void> {
    const url = window.location.toString();
    const domain = url.substring(0, url.indexOf('/', 8));
    const { user, environment, feedbackApplicationId } = context as DxAssistantContext;
    const applicationId = feedbackApplicationId ?? 'developer';

    const content = `Feedback sent from ${domain}
Full URL: ${url}
Application ID: ${applicationId}
Context:
Message ID: ${message.id}
Conversation ID: ${message.conversationId}

###

${explanations}
`;

    const body = {
      content: btoa(encodeURIComponent(content)),
      date: new Date().toISOString(),
      email: user?.mail ?? '',
      from: user?.mail ?? '',
      username: user?.name ?? '',
      rating: reaction === 'like' ? 5 : 1,
      subject: `[DX-Assistant User feedback] ${reaction}`,
      url,
      userAgent: window.navigator.userAgent,
      topicId: 'ai-message-reaction', // Useful?
    };

    const feedbackApi = `${FEEDBACK_URL[environment]}?application-id=${applicationId}`;

    return this.callApi(feedbackApi, 'POST', JSON.stringify(body));
  }
}
